import { useCallback, useRef } from 'react';

/**
 * Custom hook for debouncing function calls
 * Used to prevent excessive API calls when a function is called rapidly
 * @param fn Function to debounce
 * @param delay Delay in milliseconds (default: 1000ms)
 * @returns Debounced function with the same signature as the input function
 */
export const useDebounce = <T extends (...args: unknown[]) => unknown>(
    fn: T,
    delay = 1000,
) => {
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    return useCallback(
        (...args: Parameters<T>): void => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                fn(...args);
                timerRef.current = null;
            }, delay);
        },
        [fn, delay],
    ) as (...args: Parameters<T>) => ReturnType<T>;
};
